import React, { useState } from "react";
import { AccountBrand, SearchBrandItem } from "@/screens/analysis/types";
import { RiCloseLine } from "@remixicon/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TooltipTrigger } from "@radix-ui/react-tooltip";
import { Tooltip, TooltipContent } from "@/components/ui/tooltip";

type Props = {
  allSelectedBrands: (AccountBrand | SearchBrandItem)[];
  onRemoveBrand: (brand: AccountBrand | SearchBrandItem) => void;
};

const SelectedBrands = ({ allSelectedBrands, onRemoveBrand }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleRemoveBrand = (
    brand: AccountBrand | SearchBrandItem,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    onRemoveBrand(brand);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="h-[32px] flex items-center gap-[6px] p-[6px] rounded-[8px] squircle squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white shadow-sm hover:bg-gray-50 transition-colors"
            >
              {allSelectedBrands.slice(0, 3).map((brand) => {
                if ("profile_picture_url" in brand) {
                  return (
                    <div key={brand.page_id} className="flex items-center gap-2">
                      {brand.profile_picture_url && (
                        <img
                          src={brand.profile_picture_url}
                          alt={brand.page_name}
                          className="w-[20px] h-[20px] rounded-[7px] object-contain shrink-0"
                        />
                      )}
                    </div>
                  );
                } else {
                  return (
                    <div key={brand.id} className="flex items-center gap-2">
                      {brand.profile_image_url && (
                        <img
                          src={brand.profile_image_url}
                          alt={brand.name}
                          className="w-[18px] h-[18px] rounded-[6px] object-cover shrink-0"
                        />
                      )}
                    </div>
                  );
                }
              })}
              {allSelectedBrands.length > 3 && (
                <span className="mr-[6px] text-[12px] text-brand-primary-800 font-normal leading-[19.157px] tracking-[0.24px]">
                  +{allSelectedBrands.length - 3}
                </span>
              )}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent hidden={isOpen} side="bottom">
          <p>Show selected brands</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto p-1 rounded-[12px] border-none squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white"
        align="start"
        sideOffset={4}
      >
        <div className="">
          {allSelectedBrands.map((brand) => {
            const brandName =
              "profile_picture_url" in brand ? brand.page_name : brand.name;
            const brandImage =
              "profile_picture_url" in brand
                ? brand.profile_picture_url
                : brand.profile_image_url;

            return (
              <div
                key={"profile_picture_url" in brand ? brand.page_id : brand.id}
                className="group flex justify-between items-center gap-4 h-[28px] px-2 py-1 hover:bg-hover-gray transition-colors self-stretch"
              >
                <div className="flex items-center gap-2">
                  {/* Brand Logo */}
                  <div className="w-[18px] h-[18px] rounded-[6px] overflow-hidden flex items-center justify-center shrink-0">
                    {brandImage ? (
                      <img
                        src={brandImage}
                        alt={brandName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-[10px] font-medium text-gray-500">
                          {brandName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Brand Name */}
                  <div className="flex-1 min-w-0">
                    <span className="text-[12px] text-brand-primary-800 font-normal truncate">
                      {brandName}
                    </span>
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={(e) => handleRemoveBrand(brand, e)}
                  aria-label={`Remove ${brandName}`}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                >
                  <RiCloseLine size={14} className="text-brand-primary-400 shrink-0" />
                </button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SelectedBrands;
